import axios from 'axios';
import { WebToNativeMessage } from '../Common/Utils';


// --- Axios Instance Setup ---
const apiClient = axios.create({
    timeout: 15000, // Increased timeout for native communication
});

// --- Token Management and Waiting Logic for Native Response ---
let isFetchingTokenFromNative = false;
let pendingTokenPromises = [];

export const notifyTokenReceived = (token) => {
    if (token) {
        console.log("got new token from native:", token);
        const newToken = token;
        document.dispatchEvent(new CustomEvent('tokenRefreshLoad', { detail: { showLoader: false } }));
        pendingTokenPromises.forEach(({ resolve }) => resolve(newToken));
        pendingTokenPromises = [];


        isFetchingTokenFromNative = false;
        console.log("JS: Interceptor notified of new token from native.");
    } else {
        console.warn("JS: notifyTokenReceived received data not matching AUTH_TOKEN type/format. Data:", token);
        const error = new Error("Native provided invalid token data or unknown type for AUTH_TOKEN.");
        pendingTokenPromises.forEach(({ reject }) => reject(error));
        pendingTokenPromises = [];
        isFetchingTokenFromNative = false;
    }
};

const requestTokenFromNative = () => {
    return new Promise((resolve, reject) => {
        pendingTokenPromises.push({ resolve, reject });

        if (!isFetchingTokenFromNative) {
            isFetchingTokenFromNative = true;
            console.log("JS: Initiating request for AUTH_TOKEN from Native application...");
            WebToNativeMessage({ type: "accessTokenFail", fromPage: "wallet" });
            document.dispatchEvent(new CustomEvent('tokenRefreshLoad', { detail: { showLoader: true } }));
            console.log("JS: Waiting for token from Native application...");

            setTimeout(() => {
                if (isFetchingTokenFromNative) {
                    console.error("JS: Timeout waiting for token from Native application.");
                    const timeoutError = new Error("Timeout: Failed to receive token from Native within the expected time.");
                    pendingTokenPromises.forEach(({ reject }) => reject(timeoutError));
                    pendingTokenPromises = [];
                    isFetchingTokenFromNative = false;
                    WebToNativeMessage({ type: 'closeClient' })
                    reject(timeoutError);
                }
            }, 10000);
        }
    });
};

// --- Debounce Logic for All Requests ---
const debounceMap = new Map();
const DEBOUNCE_INTERVAL = 500; // ms

function getDebounceKey(config) {
    const { method, url, params, data } = config;
    return [
        method,
        url,
        JSON.stringify(params || {}),
        JSON.stringify(data || {})
    ].join('|');
}

// --- Axios Request Interceptor ---
apiClient.interceptors.request.use(
    async (config) => {
        // Debounce for all requests
        const key = getDebounceKey(config);
        const now = Date.now();

        if (debounceMap.has(key)) {
            const { timestamp, promise } = debounceMap.get(key);
            if (now - timestamp < DEBOUNCE_INTERVAL) {
                // Return the same promise for duplicate requests
                return promise;
            }
        }

        // Wrap the rest of your logic in a promise and store it
        const promise = (async () => {
            let accessToken = sessionStorage.getItem('token');
            console.log("JS: Axios request interceptor triggered for URL:", config.url);
            console.log("JS: Current access token from sessionStorage:", accessToken);
            return config;
        })();

        debounceMap.set(key, { timestamp: now, promise });
        setTimeout(() => debounceMap.delete(key), DEBOUNCE_INTERVAL);

        return promise;
    },
    (error) => {
        console.error("JS: Axios request setup error:", error);
        return Promise.reject(error);
    }
);

// --- Error Code Config ---
// Centralized error code config for easy management and extension
export const ERROR_CODES = {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    SERVER_ERROR: 500,
    NOT_IMPLEMENTED: 501,
    BAD_GATEWAY: 502,
    SERVICE_UNAVAILABLE: 503,
    API_FAILURE:1000
};

// --- Axios Response Interceptor ---
apiClient.interceptors.response.use(
    (response) => {
        console.log("JS: API response success:", response.status, response.config.url);
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (
            error.response &&
            (Object.values(ERROR_CODES).includes(error.response.status)) &&
            !originalRequest._retry
        ) {
            if( error.response.status === ERROR_CODES.API_FAILURE) {
                console.error("JS: API failure detected. Sending logout message to Native.");
                WebToNativeMessage({ action: 'LOGOUT_USER', reason: 'api_failure' });
                WebToNativeMessage({ type: 'closeClient' });
            }
            originalRequest._retry = true;

            console.warn("JS: API returned 401 (Unauthorized). Initiating token refresh from Native and retrying original request.");

            try {
                const newToken = await requestTokenFromNative();

                originalRequest.headers['Authorization'] = `${newToken}`;

                console.log("JS: New token acquired, retrying original request:", originalRequest);
                return apiClient(originalRequest);

            } catch (refreshError) {
                console.error("JS: Failed to get new token from Native after 401. Cannot retry request.", refreshError);

                WebToNativeMessage({ action: 'LOGOUT_USER', reason: 'token_refresh_failed' });

                return Promise.reject(refreshError);
            }
        }

        console.error("JS: API response error (not 401 or already retried):", error);
        return Promise.reject(error);
    }
);

// --- Export the Axios Instance ---
export default apiClient;
